import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalMessageOpenerService } from '@libraries/component-library';
import { Language } from '@libraries/design-system';
import { FeaturesService } from '@libraries/feature-flags';
import { GoogleAnalyticsService } from '@libraries/google-analytics';
import { ProjectsService } from '@libraries/manager-client';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { CookieService } from 'ngx-cookie-service';
import { of } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  map,
  switchMap,
  takeWhile,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import * as format from '../../helpers';
import { MonthlyCalculatorService } from '../../services/monthly-calculator.service';
import * as prequalActions from '../actions/prequal.actions';
import {
  CreateProjectPayload,
  CreateProjectPayloadPreQualification,
  MonthlyCostCalcParams,
  ProjectSuccess,
} from '../models';
import {
  formatPreferredLanguage,
  getAddressSelectPayload,
  getIneligiblePropertyData,
  getToken,
  showUnsecuredPage,
} from '../selectors';

@Injectable()
export class PrequalEffects {
  constructor(
    private actions$: Actions,
    private projectService: ProjectsService,
    private store: Store,
    private cookies: CookieService,
    private calcService: MonthlyCalculatorService,
    private globalMessages: GlobalMessageOpenerService,
    private features: FeaturesService,
    private router: Router,
    private analyticsService: GoogleAnalyticsService,
    private transloco: TranslocoService
  ) {}

  // API CALLS EFFECTS //
  handleCreateProject$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(prequalActions.CreateProject),
      withLatestFrom(this.store.pipe(select(formatPreferredLanguage))),
      map(([{ payload }, preferred_language]) => {
        // Check for Promo Code
        const promoCode = this.cookies.get('session_promo_code');
        if (promoCode) payload.promocode = promoCode;
        return format.formatToCreateProjectPayload(payload, preferred_language);
      }),
      concatMap((data: CreateProjectPayloadPreQualification) => {
        const payload = {
          ...data,
          skip_pace: this.features.isOn('yt-19374-pace-pause-prequal-updates'),
        };
        return this.projectService.propertySearch(payload).pipe(
          map((project: ProjectSuccess) =>
            prequalActions.CreateProjectSuccess({ payload: project })
          ),
          catchError((errorResp: any) => {
            const errorObj = errorResp.error;
            const { unsecuredUrl } = errorObj;
            const shouldRedirectToUnsecured =
              this.features.isOn('yt-18411-unsecured-product-phase-2-po') && !!unsecuredUrl;

            if (shouldRedirectToUnsecured) {
              return of(prequalActions.openUnsecuredUrl({ unsecuredUrl }));
            }

            switch (errorObj.error) {
              case 'MAY_BE_ELIGIBLE':
                return of(prequalActions.MayBeEligible({ payload: errorObj }));
              case 'MULTIPLE_PROPERTIES':
                return of(
                  prequalActions.MultipleAddressFound({
                    payload: errorObj.properties,
                  })
                );
              case 'OUT_OF_DISTRICT':
                return of(prequalActions.AddressOutOfDistrict({ payload: errorObj }));
              default:
                return of(prequalActions.CreateProjectFail({ payload: errorResp }));
            }
          })
        );
      })
    );
  });

  openUnsecuredUrl$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(prequalActions.openUnsecuredUrl),
        tap(({ unsecuredUrl }) => (window.location.href = unsecuredUrl))
      ),
    {
      dispatch: false,
    }
  );

  handleCreateProjectFromSelection$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(prequalActions.SelectAddress),
      withLatestFrom(
        this.store.pipe(select(getAddressSelectPayload)),
        this.store.pipe(select(formatPreferredLanguage))
      ),
      map(([{ payload }, prebuiltPayload, preferred_language]) => {
        const ambassador_referral_code = (<any>window).mbsy_short_code;

        return {
          ...prebuiltPayload,
          property_search: {
            ...payload,
            preferred_language,
            ...(ambassador_referral_code && { ambassador_referral_code }),
          },
        };
      }),
      concatMap((data: CreateProjectPayload) => {
        const payload = {
          ...data,
          skip_pace: this.features.isOn('yt-19374-pace-pause-prequal-updates'),
        };
        return this.projectService.propertySearch(payload).pipe(
          map((project: ProjectSuccess) =>
            prequalActions.CreateProjectSuccess({ payload: project })
          ),
          catchError((errorResp: any) => {
            const errorObj = errorResp.error;
            const { unsecuredUrl } = errorObj;
            const shouldRedirectToUnsecured =
              this.features.isOn('yt-18411-unsecured-product-phase-2-po') && !!unsecuredUrl;

            if (shouldRedirectToUnsecured) {
              return of(prequalActions.openUnsecuredUrl({ unsecuredUrl }));
            }

            switch (errorObj.error) {
              case 'MAY_BE_ELIGIBLE':
                return of(prequalActions.MayBeEligible({ payload: errorObj }));
              case 'MULTIPLE_PROPERTIES':
                return of(
                  prequalActions.MultipleAddressFound({
                    payload: errorObj.properties,
                  })
                );
              case 'OUT_OF_DISTRICT':
                return of(prequalActions.AddressOutOfDistrict({ payload: errorObj }));
              default:
                return of(prequalActions.CreateProjectFail({ payload: errorResp }));
            }
          })
        );
      })
    );
  });

  submitIneligibleProperty$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(prequalActions.SubmitIneligibleProperty),
      map((action) => action.payload),
      withLatestFrom(this.store.pipe(select(getIneligiblePropertyData))),
      map(([payload, emailAndAddressAndBoundaryCode]) =>
        format.formatToCreateIneligiblePropertyPayload({
          ...payload,
          ...emailAndAddressAndBoundaryCode,
        })
      ),
      concatMap((payload) =>
        this.projectService.createIneligibleProperty(payload).pipe(
          map((result) => prequalActions.SubmitIneligiblePropertySuccess({ payload: result })),
          catchError((errorResponse) =>
            of(
              prequalActions.SubmitIneligiblePropertyFail({
                payload: errorResponse,
              })
            )
          )
        )
      )
    );
  });

  submitWaitlistProperty$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(prequalActions.SubmitWaitlistProperty),
      map((action) => action.payload),
      concatMap((payload) =>
        this.projectService.createIneligibleProperty(payload).pipe(
          map((result) => prequalActions.SubmitWaitlistPropertySuccess({ payload: result })),
          catchError((errorResponse) =>
            of(
              prequalActions.SubmitwaitlistPropertyFail({
                payload: errorResponse,
              })
            )
          )
        )
      )
    );
  });

  // CALCULATOR EFFECTS

  calculateMonthlyCost$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(prequalActions.CalculateMonthlyCost, prequalActions.ReCalculateMonthlyCost),
      map((action) => action.payload),
      withLatestFrom(this.store.pipe(select(getToken))),
      map(([payload, token]) => ({ ...payload, token: token })),
      switchMap((payload: MonthlyCostCalcParams) =>
        this.calcService.calculateMonthlyCost(payload).pipe(
          map((result) => prequalActions.CalculateMonthlyCostSuccess({ payload: result })),
          catchError((errorResponse) =>
            of(
              prequalActions.CalculateMonthlyCostFail({
                payload: errorResponse,
              })
            )
          )
        )
      )
    );
  });

  // // ROUTE & REDIRECT EFFECTS //

  addPropertySuccessWithUnsecuredUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(prequalActions.CreateProjectSuccess),
      map(({ payload }) => payload.unsecuredUrl),
      filter(
        (unsecuredUrl) =>
          this.features.isOn('yt-18411-unsecured-product-phase-2-po') && !!unsecuredUrl
      ),
      map((unsecuredUrl) =>
        prequalActions.openUnsecuredUrl({
          unsecuredUrl: <string>unsecuredUrl,
        })
      )
    )
  );

  pacePauseRedirectToUnsecuredUponPropertySearchSuccessResponse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(prequalActions.CreateProjectSuccess),
      // NOTE: When this FF is on we will get empty response from server.
      filter(() => this.features.isOn('yt-19374-pace-pause-prequal-updates')),
      map(() => prequalActions.RedirectToCongratulationsUnsecured())
    )
  );

  redirectOnProjectSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(prequalActions.CreateProjectSuccess),
        filter(
          ({ payload }) =>
            this.features.isOff('yt-19374-pace-pause-prequal-updates') &&
            (this.features.isOff('yt-18411-unsecured-product-phase-2-po') || !payload.unsecuredUrl)
        ),
        filter(
          ({ payload }) =>
            this.features.isOff('yt-13358-prequal-calculator') || !payload?.monthly_costs
        ),
        tap(() => {
          this.router.navigate(['/prequal', { outlets: { right: 'success' } }], {
            queryParamsHandling: 'preserve',
          });
        })
      ),
    { dispatch: false }
  );

  redirectToPrequalCalculator$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(prequalActions.CreateProjectSuccess),
        takeWhile(() => this.features.isOn('yt-13358-prequal-calculator')),
        map(({ payload }) => payload.monthly_costs),
        filter((monthlyCosts) => !!monthlyCosts),
        map((monthlyCosts) => (monthlyCosts.length ? 'success-improvement' : 'success-other')),
        tap((path) => {
          this.router.navigateByUrl(`/prequal/${path}`);
        })
      ),
    { dispatch: false }
  );

  redirectOnMultipleProjectsFound$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(prequalActions.MultipleAddressFound),
        tap(() =>
          this.router.navigate(['/prequal', { outlets: { right: 'addresses' } }], {
            queryParamsHandling: 'preserve',
          })
        )
      ),
    { dispatch: false }
  );

  redirectToUnsecuredComingSoon$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(prequalActions.AddressOutOfDistrict),
        withLatestFrom(this.store.pipe(select(showUnsecuredPage))),
        filter(
          ([action, showUnsecured]) =>
            this.features.isOn('yn-167-unsecured-mvp-po') && showUnsecured
        ),
        tap(() => {
          this.router.navigate(['/prequal', { outlets: { right: 'congratulations-unsecured' } }], {
            queryParamsHandling: 'preserve',
          });
        })
      ),
    { dispatch: false }
  );

  redirectToComingSoon$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(prequalActions.AddressOutOfDistrict),
        withLatestFrom(this.store.pipe(select(showUnsecuredPage))),
        filter(
          ([action, showUnsecured]) =>
            this.features.isOff('yn-167-unsecured-mvp-po') || !showUnsecured
        ),
        tap(() => {
          this.router.navigate(['/prequal', { outlets: { right: 'coming-soon' } }], {
            queryParamsHandling: 'preserve',
          });
        })
      ),
    { dispatch: false }
  );

  redirectToCongratulationsUnsecured$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(prequalActions.RedirectToCongratulationsUnsecured),
        tap(() =>
          this.router.navigate(['/prequal', { outlets: { right: 'congratulations-unsecured' } }], {
            queryParamsHandling: 'preserve',
          })
        )
      ),
    { dispatch: false }
  );

  redirectToMayBeEligible$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(prequalActions.MayBeEligible),
        tap(() =>
          this.router.navigate(['/prequal', { outlets: { right: 'may-be-eligible' } }], {
            queryParamsHandling: 'preserve',
          })
        )
      ),
    { dispatch: false }
  );

  redirectToUnsecuredIneligiblePropertySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(prequalActions.SubmitIneligiblePropertySuccess),
        withLatestFrom(this.store.pipe(select(showUnsecuredPage))),
        filter(
          ([action, showUnsecured]) =>
            this.features.isOn('yn-167-unsecured-mvp-po') && showUnsecured
        ),
        tap(() => this.store.dispatch(prequalActions.RedirectToCongratulationsUnsecured()))
      ),
    { dispatch: false }
  );

  redirectToIneligiblePropertySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(prequalActions.SubmitIneligiblePropertySuccess),
        withLatestFrom(this.store.pipe(select(showUnsecuredPage))),
        filter(
          ([action, showUnsecured]) =>
            this.features.isOff('yn-167-unsecured-mvp-po') || !showUnsecured
        ),
        tap(() =>
          this.router.navigate(['/prequal', { outlets: { right: 'coming-soon-success' } }], {
            queryParamsHandling: 'preserve',
          })
        )
      ),
    { dispatch: false }
  );

  redirectToApplication$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(prequalActions.RedirectToApplication),
        withLatestFrom(this.store.pipe(select(getToken))),
        tap(([action, token]) => {
          const eventCategory = 'ngrx';
          const eventAction = action.type;
          const eventLabel = token;
          const eventValue = undefined;

          this.analyticsService.sendEvent(eventCategory, eventAction, eventLabel, eventValue);
          // this.router.navigate([`/apply/${token}`]);
          // TODO: YT-17570 As temporary fix We we using location.href instead of router navigation.
          // We need to add a proper fix for scrolling issue and remove below code.
          window.location.href = `/apply/${token}`;
        })
      ),
    { dispatch: false }
  );

  handleCloseActive$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(prequalActions.CloseActive),
        tap(() =>
          this.router.navigate(['/prequal'], {
            queryParamsHandling: 'preserve',
          })
        )
      ),
    { dispatch: false }
  );

  sendAnalyticsHitEvents$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(prequalActions.CreateProjectSuccess, prequalActions.RedirectToApplication),
        withLatestFrom(this.store.pipe(select(getToken))),
        tap(([action, token]) => {
          if (action.type === prequalActions.CreateProjectSuccess.type) {
            // Project Creation Success Source
            this.analyticsService.setDimension('dimension1', 'prequal');
          }

          const eventCategory = 'ngrx';
          const eventAction = action.type;
          const eventLabel = token;
          const eventValue = undefined;

          this.analyticsService.sendEvent(eventCategory, eventAction, eventLabel, eventValue);
        })
      ),
    { dispatch: false }
  );

  //show some error dialogs if API calls go wrong
  public handleLoadError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          prequalActions.CalculateMonthlyCostFail,
          prequalActions.CreateProjectFail,
          prequalActions.ReCalculateMonthlyCostFail,
          prequalActions.SubmitIneligiblePropertyFail
        ),
        map((action) => action.payload),
        tap(() => {
          const type = 'warn';
          const body = this.transloco.translate('errors.serverError');
          const action = this.transloco.translate('errors.reloadBtn');
          const errors: string[] = [];
          const onAction = () => location && location.reload();
          this.globalMessages.open({ body, errors, action, type, onAction });
        })
      ),
    { dispatch: false }
  );

  switchLanguage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(prequalActions.SwitchPreferredLanguage),
        map(({ preferredLanguage }) => (preferredLanguage === Language.Espanol ? 'es' : 'en')),
        tap((lang: string) => this.transloco.setActiveLang(lang))
      ),
    { dispatch: false }
  );
}
